// pages/RoleAuthFileEditor.jsx
// [Tahap 2a] Editor VALUE authorization berbasis FILE download PFCG (.SAP).
// Murni di browser — TIDAK menyentuh SAP. Alur:
//   1) upload file hasil "Download" PFCG
//   2) edit value field authorization (baris AGR_1251) di UI
//   3) download file baru → upload kembali ke PFCG + generate
//
// Jaminan keamanan:
//   • Round-trip byte-identik: baris yang tidak diubah keluar persis sama.
//   • Saat value diubah, HANYA slot VALUE (80 char) di baris itu yang ditimpa;
//     MANDT/AGR/COUNTER/OBJECT/AUTH/FIELD/trailer/counter-node tidak disentuh.
//   • Encoding Latin-1 (ISO-8859-1) & line ending CRLF dipertahankan.
//   • Tahap 2a HANYA ubah value; tambah/hapus object & org level belum didukung
//     (butuh manajemen counter AGR_1250/AGR_1252 → tahap 2b).
import React, { useMemo, useRef, useState } from 'react';
import { X, Upload, Download, Search, AlertTriangle, FileText, RotateCcw } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

// ── Layout kolom fixed-width baris AGR_1251 (absolut dari awal baris) ──
// [0:50] tag | 50:53 MANDT | 53:83 AGR_NAME | 83:89 COUNTER | 89:99 OBJECT
// 99:111 AUTH | 111:115 gap | 115:125 FIELD | 125:205 VALUE(80) | 205: trailer
const V_START = 125, V_END = 205, V_WIDTH = 80;
const O_START = 89, O_END = 99, A_START = 99, A_END = 111, F_START = 115, F_END = 125;

const decodeLatin1 = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s;
};
const encodeLatin1 = (str) => {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i) & 0xff;
  return out;
};

export default function RoleAuthFileEditor({ onClose, id = true, presetName = '', presetBase64 = '' }) {
  const {
    t
  } = useLang();

  const [fileName, setFileName] = useState('');
  const [rawLines, setRawLines] = useState(null);  // baris asli TANPA '\r' (disimpan terpisah)
  const [eol, setEol] = useState('\r\n');
  const [roleName, setRoleName] = useState('');
  const [roleText, setRoleText] = useState('');
  const [rows, setRows] = useState([]);            // [{lineIdx, object, auth, field, orig, value}]
  const [edits, setEdits] = useState({});          // lineIdx → nilai baru (trim)
  const [search, setSearch] = useState('');
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const parse = (raw) => {
    const useCrlf = raw.includes('\r\n');
    setEol(useCrlf ? '\r\n' : '\n');
    // Simpan baris tanpa '\r' agar indexing kolom konsisten; '\r' ditambah lagi saat export.
    const lines = raw.split('\n').map(l => l.replace(/\r$/, ''));
    const parsedRows = [];
    let rName = '', rText = '';
    lines.forEach((l, i) => {
      const tag = l.slice(0, 50).trim();
      if (tag === 'LOADED_AGRS' && !rName) rName = l.slice(50).trim();
      if (tag === 'AGR_TEXTS') {
        // payload: MANDT(3)+AGR(30)+SPRAS(1?) ... teks di ekor. Ambil setelah kolom ~89.
        const t = l.slice(89).trim();
        if (t && !rText) rText = t;
      }
      if (tag === 'AGR_1251' && l.length >= V_END) {
        parsedRows.push({
          lineIdx: i,
          object: l.slice(O_START, O_END).trim(),
          auth:   l.slice(A_START, A_END).trim(),
          field:  l.slice(F_START, F_END).trim(),
          orig:   l.slice(V_START, V_END).replace(/\s+$/,''),   // value asli (trailing space dibuang utk tampil)
          value:  l.slice(V_START, V_END).replace(/\s+$/,''),
        });
      }
    });
    return { lines, rows: parsedRows, rName, rText };
  };

  const loadRaw = (raw, name) => {
    setErr(null);
    const { lines, rows: pr, rName, rText } = parse(raw);
    if (!pr.length) setErr(t('roleAuthEd.noAgr1251RowsFound'));
    setFileName(name);
    setRawLines(lines);
    setRows(pr);
    setEdits({});
    setRoleName(rName || '');
    setRoleText(rText || '');
  };

  const onFile = async (file) => {
    setErr(null);
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      loadRaw(decodeLatin1(buf), file.name);
    } catch (e) {
      setErr((t('roleAuthEd.failedReadFile')) + e.message);
    }
  };

  // Muat konten preset (mis. hasil download dari SAP, base64 Latin-1).
  React.useEffect(() => {
    if (!presetBase64) return;
    try {
      const cleanB64 = String(presetBase64 || '').replace(/[^A-Za-z0-9+/=]/g, '');
      const bin = atob(cleanB64);            // string biner (charCode = byte)
      loadRaw(bin, presetName || 'ROLE.SAP');
    } catch (e) {
      setErr((t('roleAuthEd.failedLoadFileSap')) + e.message);
    }
    // eslint-disable-next-line
  }, [presetBase64]);

  const setEdit = (lineIdx, orig, v) => {
    setEdits(prev => {
      const n = { ...prev };
      if (v === orig) delete n[lineIdx];   // kembali ke asli = bukan perubahan
      else n[lineIdx] = v;
      return n;
    });
  };

  const changeCount = Object.keys(edits).length;
  const overLimit = Object.values(edits).some(v => v.length > V_WIDTH);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return rows;
    return rows.filter(r => r.object.toUpperCase().includes(q) || r.field.toUpperCase().includes(q) || r.auth.toUpperCase().includes(q));
  }, [rows, search]);

  // Kelompokkan per object → auth untuk tampilan.
  const grouped = useMemo(() => {
    const m = {};
    for (const r of filtered) {
      const g = m[r.object] || (m[r.object] = {});
      (g[r.auth] = g[r.auth] || []).push(r);
    }
    return Object.entries(m).map(([object, auths]) => ({ object, auths: Object.entries(auths).map(([auth, fields]) => ({ auth, fields })) }));
  }, [filtered]);

  const download = () => {
    if (!rawLines) return;
    if (overLimit) { setErr(t('roleAuthEd.someValuesExceedVWidth', {
      V_WIDTH
    })); return; }
    // Bangun ulang: baris tak berubah dipakai apa adanya; baris teredit ditimpa slot VALUE saja.
    const out = rawLines.map((l, i) => {
      if (!(i in edits)) return l;
      const v = edits[i].padEnd(V_WIDTH, ' ').slice(0, V_WIDTH);
      return l.slice(0, V_START) + v + l.slice(V_END);
    });
    const text = out.join(eol);
    const blob = new Blob([encodeLatin1(text)], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const base = fileName.replace(/\.(sap|SAP)$/, '');
    a.href = url; a.download = `${base}_EDITED.SAP`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const reset = () => { setEdits({}); setErr(null); };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-slate-800 flex items-center gap-2">
              <FileText className="text-blue-500" style={{ width: 18, height: 18 }} />
              {t('roleAuthEd.authorizationValueEditorPfcgFile')}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('roleAuthEd.editFieldValuesThenUpload')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"><X style={{ width: 18, height: 18 }} /></button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".sap,.SAP" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold bg-white border border-slate-200 hover:border-blue-300">
            <Upload style={{ width: 14, height: 14 }} /> {t('roleAuthEd.uploadSapFile')}
          </button>
          {fileName && (
            <span className="text-xs text-slate-500 truncate">
              {fileName}{roleName ? ` · ${roleName}` : ''}{roleText ? ` — ${roleText}` : ''}
              {rows.length ? ` · ${rows.length} value` : ''}
            </span>
          )}
          {rows.length > 0 && (
            <div className="relative ml-auto min-w-[160px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" style={{ width: 14, height: 14 }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={t('roleAuthEd.searchObjectField')}
                className="w-full pl-8 pr-3 py-1.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300" />
            </div>
          )}
        </div>

        {err && (
          <div className="mx-5 mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <AlertTriangle style={{ width: 15, height: 15 }} className="shrink-0 mt-0.5" /> <span>{err}</span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {!rawLines && (
            <div className="text-center text-slate-400 text-sm py-16">
              {t('roleAuthEd.uploadPfcgRoleDownloadFile')}
            </div>
          )}
          {rawLines && !grouped.length && (
            <div className="text-center text-slate-400 text-sm py-10">{t('roleAuthEd.noMatchingValues')}</div>
          )}
          {grouped.map(g => (
            <div key={g.object} className="mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono text-sm font-bold text-slate-700">{g.object}</span>
              </div>
              <div className="space-y-2">
                {g.auths.map(({ auth, fields }) => (
                  <div key={auth} className="rounded-xl border border-slate-200 bg-slate-50/50 p-2.5">
                    <p className="text-[10px] font-mono text-slate-400 mb-1.5">{auth}</p>
                    <div className="space-y-1.5">
                      {fields.map(r => {
                        const val = (r.lineIdx in edits) ? edits[r.lineIdx] : r.value;
                        const changed = r.lineIdx in edits;
                        const tooLong = val.length > V_WIDTH;
                        return (
                          <div key={r.lineIdx} className="flex items-center gap-2">
                            <span className="font-mono text-[11px] text-slate-500 w-28 shrink-0 truncate" title={r.field}>{r.field}</span>
                            <input
                              value={val}
                              onChange={(e) => setEdit(r.lineIdx, r.orig, e.target.value)}
                              className={`flex-1 px-2 py-1 rounded-lg border text-[11px] font-mono outline-none
                                ${tooLong ? 'border-rose-300 bg-rose-50' : changed ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200 bg-white'} focus:border-blue-400`}
                            />
                            <span className={`text-[10px] w-10 text-right shrink-0 ${tooLong ? 'text-rose-500 font-semibold' : 'text-slate-300'}`}>{val.length}/{V_WIDTH}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {rawLines && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              {changeCount > 0
                ? <span className="text-blue-600 font-semibold">{changeCount} {t('roleAuthEd.valuesChanged')}</span>
                : (t('roleAuthEd.noChangesYet'))}
              {overLimit && <span className="text-rose-500 font-semibold ml-2">{t('roleAuthEd.someValuesTooLong')}</span>}
            </span>
            <div className="flex gap-2">
              {changeCount > 0 && (
                <button onClick={reset} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">
                  <RotateCcw style={{ width: 14, height: 14 }} /> {id ? 'Reset' : 'Reset'}
                </button>
              )}
              <button onClick={download} disabled={overLimit}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold ${overLimit ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                <Download style={{ width: 15, height: 15 }} /> {t('roleAuthEd.downloadResult')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
